import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { playAcknowledgement } from "../audio";
import { DAY_NAMES, describeCadence, formatDateTime, ordinal } from "../format";
import { navigate } from "../router";
import { deviceTimezone } from "../store";
import type { Cadence, Schedule } from "../../shared/types";
import { PhotoPicker } from "../components/PhotoPicker";
import { ErrorBox, ScreenTitle, Spinner } from "../components/Shell";
import { useBountyUI, useProjectsUI } from "../features";
import { jobSizeLabel } from "../../shared/projects.ts";
import { BusyLabel } from "../components/BusyLabel";
import { schedulesChanged } from "./board-patch";
import { setLastNotice } from "../award";
// The server's own occurrence math, pure and import-free (shared/schedule-early.ts reads it the same way).
import { nextOccurrenceAfter } from "../../server/schedule.ts";
import { radioKeys } from "../radio-group.ts";
import { scheduleMissing, type Missing } from "../../shared/form-missing.ts";

/** "America/Toronto" as a person says it: "Toronto". */
function zonePlace(tz: string): string {
  return (tz.split("/").pop() ?? tz).replace(/_/g, " ");
}

/** Edit of a schedule with no before photo (docs/product.md "Schedules (2026-10-01)"). */
const NEEDS_PHOTO = "Add a before photo so its chores have one.";

interface Props {
  /** Undefined when creating. */
  id?: string;
  peonName: string;
}

export function ScheduleForm({ id, peonName }: Props) {
  const editing = !!id;
  const bountyUIEnabled = useBountyUI();
  // Projects (docs/projects.md): with projectsUI on the pill reads "Project".
  const projectsUIEnabled = useProjectsUI();
  const [loaded, setLoaded] = useState<Schedule | null>(null);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  // As on Add Task: the note hides behind "+ Add note" until asked for; a note with text stays visible.
  const [notesOpen, setNotesOpen] = useState(false);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (notesOpen) notesRef.current?.focus();
  }, [notesOpen]);
  const [bigJob, setBigJob] = useState(false);
  const [cadence, setCadence] = useState<Cadence>("daily");
  const [time, setTime] = useState("09:00");
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [active, setActive] = useState(true);
  const [startNow, setStartNow] = useState(false);
  const [busy, setBusy] = useState(false);
  /** The busy write is the delete, so Delete reads as working and Save does not. */
  const [deleting, setDeleting] = useState(false);
  /** The busy write is the pause or resume, so that button reads as working and Save does not. */
  const [pausing, setPausing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Save or delete failed: shown above Save, not under the title (that is the load error). */
  const [saveError, setSaveError] = useState<string | null>(null);

  // Bumped by "Try again" after a failed load so the effect runs once more.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!id) return;
    setError(null);
    api.schedules
      .list()
      .then((all) => {
        const s = all.find((x) => x.id === id);
        if (!s) throw new Error("Schedule not found");
        setLoaded(s);
        setTitle(s.title);
        setNotes(s.notes);
        setBigJob(Boolean(s.big_job));
        setCadence(s.cadence);
        setTime(s.time_of_day);
        setDayOfWeek(s.day_of_week ?? 1);
        setDayOfMonth(s.day_of_month ?? 1);
        setPhotoId(s.start_photo_id);
        setActive(!!s.active);
      })
      .catch((e) => setError(e.message));
  }, [id, attempt]);

  // Save stays live while the form is incomplete (docs/ui-audit.md triage item 1): a press says the
  // first missing thing and puts the focus on it. The line goes once that field is filled.
  const formRef = useRef<HTMLFormElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const [missing, setMissing] = useState<Missing | null>(null);
  const unready = scheduleMissing({ title, time, photoId });
  useEffect(() => {
    // As an update: a press that set a new line before this effect ran keeps it.
    if (missing && unready?.field !== missing.field) setMissing((m) => (m && unready?.field !== m.field ? null : m));
  }, [missing, unready?.field]);

  async function submit(e: Event) {
    e.preventDefault();
    if (busy) return;
    if (unready) {
      setSaveError(null);
      setMissing(unready);
      if (unready.field === "title") titleInputRef.current?.focus();
      else if (unready.field === "time") timeInputRef.current?.focus();
      else formRef.current?.querySelector<HTMLElement>(".photo-placeholder")?.focus();
      return;
    }
    void playAcknowledgement();
    setBusy(true);
    setSaveError(null);
    const fields = {
      title: title.trim(),
      notes: notes.trim(),
      big_job: bigJob,
      cadence,
      time_of_day: time,
      day_of_week: cadence === "weekly" ? dayOfWeek : null,
      day_of_month: cadence === "monthly" ? dayOfMonth : null,
      timezone: deviceTimezone(),
      start_photo_id: photoId,
    };
    try {
      if (id) {
        schedulesChanged({ saved: await api.schedules.update(id, { ...fields, active }) });
        navigate({ name: "schedules" }, true);
      } else {
        const saved = await api.schedules.create({ ...fields, created_by: peonName, start_now: startNow });
        schedulesChanged({ saved });
        const first = saved.next_due_at ?? firstAt;
        setLastNotice(startNow ? "Schedule added. First task posted." : first ? `Schedule added. First task ${formatDateTime(first)}.` : "Schedule added.");
        navigateAfterCreate();
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  function navigateAfterCreate() {
    navigate(startNow ? { name: "open" } : { name: "schedules" }, true);
  }

  /**
   * Pause schedule / Resume schedule (owner, 2026-10-21: a button in place of the Active checkbox).
   * A pause is an action, so it saves at once: the same PATCH the checkbox's Save sent, with only
   * `active`, which the server allows even for a schedule still waiting for its photo. Any other edit
   * on the form stays unsaved until Save changes. The button flips in place.
   */
  async function togglePaused() {
    if (!id || busy) return;
    setBusy(true);
    setPausing(true);
    setSaveError(null);
    try {
      const saved = await api.schedules.update(id, { active: !active });
      schedulesChanged({ saved });
      setLoaded(saved);
      setActive(!!saved.active);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setBusy(false);
      setPausing(false);
    }
  }

  async function remove() {
    if (!id || !confirm("Delete this schedule? Existing tasks it created are kept.")) return;
    setBusy(true);
    setDeleting(true);
    setSaveError(null);
    try {
      await api.schedules.remove(id);
      schedulesChanged({ removed: id });
      navigate({ name: "schedules" }, true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not delete");
      setBusy(false);
      setDeleting(false);
    }
  }

  // A schedule made before the photo rule: Save waits for one, and the form says why.
  const needsPhoto = editing && !!loaded && !photoId;

  // When the first chore appears if it is not posted now: the next time this cadence fires, by the
  // server's rule, in this device's zone (saving sets the schedule to it).
  const firstAt = (() => {
    if (!/^\d{1,2}:\d{2}$/.test(time)) return null;
    try {
      const at = nextOccurrenceAfter(
        { cadence, time_of_day: time, day_of_week: cadence === "weekly" ? dayOfWeek : null, day_of_month: cadence === "monthly" ? dayOfMonth : null, timezone: deviceTimezone() },
        new Date(),
      );
      return at ? at.toISOString() : null;
    } catch {
      return null;
    }
  })();

  const preview = describeCadence({ cadence, time_of_day: time || "00:00", day_of_week: dayOfWeek, day_of_month: dayOfMonth });

  return (
    <>
      <main class="content">
        <ScreenTitle title={editing ? "Edit schedule" : "New schedule"} />
        {editing && !loaded && !error && <Spinner />}
        {error && <ErrorBox message={error} onRetry={id && !loaded ? () => setAttempt((n) => n + 1) : undefined} />}
        {(!editing || loaded) && (
          <form class="form" ref={formRef} noValidate onSubmit={submit}>
            <label class="field">
              <span class="field-label">What needs doing?</span>
              <input ref={titleInputRef} class="input" type="text" required maxLength={200} value={title} onInput={(e) => setTitle(e.currentTarget.value)} />
            </label>
            {notesOpen || notes ? (
              <label class="field">
                <span class="field-label">Note</span>
                <textarea ref={notesRef} class="input" rows={3} maxLength={4000} value={notes} onInput={(e) => setNotes(e.currentTarget.value)} />
              </label>
            ) : (
              <button type="button" class="btn btn-ghost add-note" onClick={() => setNotesOpen(true)}>
                + Add note
              </button>
            )}

            {/* The same pill as Add Task's Big job, under its photo. */}
            {bountyUIEnabled && (
              <div class="task-flag-pills" role="group" aria-label="This schedule">
                <button type="button" class={`task-flag-pill task-flag-big${bigJob ? " on" : ""}`} aria-pressed={bigJob} disabled={busy} onClick={() => setBigJob(!bigJob)}>
                  <span class="pill-check" aria-hidden="true" />
                  {jobSizeLabel(projectsUIEnabled)}
                </button>
              </div>
            )}
            <div class="field">
              <span class="field-label" id="schedule-repeats-label">Repeats</span>
              <div class="segmented" role="radiogroup" aria-labelledby="schedule-repeats-label" onKeyDown={radioKeys}>
                {(["daily", "weekly", "monthly"] as Cadence[]).map((c) => (
                  <button key={c} type="button" class={cadence === c ? "active" : ""} role="radio" aria-checked={cadence === c} onClick={() => setCadence(c)}>
                    {c[0].toUpperCase() + c.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {cadence === "weekly" && (
              <label class="field">
                <span class="field-label">On</span>
                <select class="input" value={dayOfWeek} onChange={(e) => setDayOfWeek(Number(e.currentTarget.value))}>
                  {DAY_NAMES.map((d, i) => (
                    <option key={i} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {cadence === "monthly" && (
              <label class="field">
                <span class="field-label">On the</span>
                <select class="input" value={dayOfMonth} onChange={(e) => setDayOfMonth(Number(e.currentTarget.value))}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      {ordinal(d)}
                      {d > 28 ? " (or last day of shorter months)" : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label class="field">
              <span class="field-label">At</span>
              <input ref={timeInputRef} class="input" type="time" required value={time} onInput={(e) => setTime(e.currentTarget.value)} />
            </label>
            <div class="muted small">
              {preview}.
              {loaded?.next_due_at && active ? ` Next: ${formatDateTime(loaded.next_due_at)}.` : ""}
              {/* Saving sets the schedule to this device's zone; only worth a word when that is a change. */}
              {loaded && loaded.timezone && loaded.timezone !== deviceTimezone()
                ? ` Set on ${zonePlace(loaded.timezone)} time. Saving moves it to ${zonePlace(deviceTimezone())} time.`
                : ""}
            </div>

            {/* Required, as on Add Task: every chore the schedule posts carries this photo. */}
            <PhotoPicker label="Before photo" allowClear={false} photoId={photoId} onChange={setPhotoId} />

            {!editing && (
              <label class="field toggle toggle-row">
                <input type="checkbox" checked={startNow} onChange={(e) => setStartNow(e.currentTarget.checked)} />
                <span>
                  Post the first task now
                  <span class="muted small block">{firstAt ? `Otherwise the first task appears ${formatDateTime(firstAt)}.` : "Otherwise the first task appears at the next scheduled time."}</span>
                </span>
              </label>
            )}

            {/* A save or delete refusal sits right above Save, where the peon is looking. */}
            {missing ? (
              <div class="form-error" role="alert" id="schedule-missing">{missing.field === "photo" && needsPhoto ? NEEDS_PHOTO : (unready?.field === missing.field ? unready : missing).line}</div>
            ) : (
              needsPhoto && <div class="form-error">{NEEDS_PHOTO}</div>
            )}
            {saveError && <ErrorBox message={saveError} />}
            <button
              class={`btn btn-primary btn-block${unready ? " btn-unready" : ""}`}
              type="submit"
              disabled={busy}
              aria-describedby={missing ? "schedule-missing" : undefined}
            >
              <BusyLabel busy={busy && !deleting && !pausing} label="Saving…">{editing ? "Save changes" : "Create schedule"}</BusyLabel>
            </button>
            {!editing && <p class="muted small posting-as">Posting as {peonName}</p>}
            {/* A new schedule is always active; only an existing one can be paused or resumed. */}
            {editing && (
              <button type="button" class="btn btn-ghost btn-block" onClick={togglePaused} disabled={busy}>
                <BusyLabel busy={pausing}>{active ? "Pause schedule" : "Resume schedule"}</BusyLabel>
              </button>
            )}
            {editing && (
              <button type="button" class="btn btn-ghost danger" onClick={remove} disabled={busy}>
                <BusyLabel busy={deleting}>Delete schedule</BusyLabel>
              </button>
            )}
          </form>
        )}
      </main>
    </>
  );
}
